import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PERIOD_OPTIONS } from "@/lib/format";
import { CalendarDays } from "lucide-react";

export function DateFilter({ value, onChange }) {
  const { period = "this_month", start = "", end = "", ref = "" } = value || {};
  const [open, setOpen] = useState(false);

  function set(patch) {
    onChange({ period, start, end, ref, ...patch });
  }

  const current = PERIOD_OPTIONS.find((p) => p.value === period)?.label || "This Month";
  const needsCustom = ["custom", "date", "month", "year"].includes(period);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 bg-white" data-testid="date-filter-trigger">
          <CalendarDays className="h-4 w-4 text-[#1A2A5E]" />
          <span className="font-medium">{current}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 bg-white/95 backdrop-blur-md" align="end">
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Period</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1.5">
              {PERIOD_OPTIONS.map((p) => (
                <button
                  key={p.value}
                  data-testid={`period-${p.value}`}
                  onClick={() => set({ period: p.value })}
                  className={`text-xs px-2 py-1.5 rounded-md border text-left transition-colors ${
                    period === p.value ? "bg-[#1A2A5E] text-white border-[#1A2A5E]" : "bg-white border-slate-200 hover:border-[#1A2A5E]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {period === "custom" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-500">From</Label>
                <Input type="date" value={start} onChange={(e) => set({ start: e.target.value })} data-testid="date-from" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">To</Label>
                <Input type="date" value={end} onChange={(e) => set({ end: e.target.value })} data-testid="date-to" />
              </div>
            </div>
          )}
          {period === "date" && (
            <div>
              <Label className="text-xs text-slate-500">Date</Label>
              <Input type="date" value={ref} onChange={(e) => set({ ref: e.target.value })} data-testid="date-specific" />
            </div>
          )}
          {period === "month" && (
            <div>
              <Label className="text-xs text-slate-500">Month</Label>
              <Input type="month" value={ref} onChange={(e) => set({ ref: e.target.value })} data-testid="month-specific" />
            </div>
          )}
          {period === "year" && (
            <div>
              <Label className="text-xs text-slate-500">Year</Label>
              <Input type="number" placeholder="2026" value={ref} onChange={(e) => set({ ref: e.target.value })} data-testid="year-specific" />
            </div>
          )}

          <Button className="w-full bg-[#F4571E] hover:bg-[#D9420D]" onClick={() => setOpen(false)} data-testid="date-filter-apply">
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function buildParams(f) {
  if (!f) return {};
  const p = { period: f.period };
  if (f.period === "custom") { p.start = f.start; p.end = f.end; }
  if (["date", "month", "year"].includes(f.period)) p.ref = f.ref;
  return p;
}
